import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';

import { Subscription } from 'rxjs';
import { Messages } from 'src/app/constants/messages';
import { ActionModeType, ConfigEntityOnboardFormModel, DataBroadcast, DataBroadcastModel, EntityOnboardModel, EntityPage, EntityValue } from 'src/app/model';
import { DtcServiceService } from 'src/app/modules/dtc/dtc-service.service';
import { DataService, ServicecallService } from 'src/app/services';

@Component({
  selector: 'app-add-dtc',
  templateUrl: './add-dtc.component.html',
  styleUrl: './add-dtc.component.css'
})
export class AddDtcComponent implements OnInit, OnDestroy {

  @Input() dialogData: {type: string, formStatus: ActionModeType, data: any };
  @Output() dialogClose = new EventEmitter<any>();

  // Content
  content: EntityOnboardModel;

  // Configuration
  private _tenantGenericConfig: any;
  private _entityDeviceOnboardFormConfig: ConfigEntityOnboardFormModel;

  // Subscription
  private _dataServiceEventSubscription: Subscription;
  private _addDtcSubscription: Subscription
  private _updateDtcSubscription: Subscription;

  constructor(
    private _apiService: ServicecallService,
    private _dataService: DataService,
    private _dtcService: DtcServiceService,
    private _router: Router
  ) { }

  ngOnInit(): void {
    // Set configuration
    this._setConfiguration();
    // Set entity onboard content
    this._setEntityOnboardContent();
    // API Refresh
    this._dataServiceEventSubscription = this._dataService.events
      .subscribe({
        next: (broadcast: DataBroadcastModel) => {
          if (broadcast.name === DataBroadcast?.SITE_CHANGED ||
            broadcast.name === DataBroadcast.API_REFRESH
          ) {
            this._setConfiguration();
            // Set entity onboard content
            this._setEntityOnboardContent();
          }
        }
      });
  }

  ngOnDestroy(): void {
    this._dataServiceEventSubscription?.unsubscribe();
    this._addDtcSubscription?.unsubscribe();
    this._updateDtcSubscription?.unsubscribe();
  }

  /**
   * Sets configuration
   */
  private _setConfiguration() {
    this._tenantGenericConfig = this._apiService.getConfiguration('tenantGenericConfig')
      ? this._apiService.getConfiguration('tenantGenericConfig')
      : null;
    this._entityDeviceOnboardFormConfig = this._apiService.getConfiguration('entityDeviceOnboardFormConfig')
      ? this._apiService.getConfiguration('entityDeviceOnboardFormConfig')
      : null;
  }

  /**
   * Sets entity onboard content
   */
  private _setEntityOnboardContent() {
    let formFields = this._tenantGenericConfig?.dtc?.addDtcFields?.formSectionList ?? [];

    if (this.dialogData?.type === 'dtcDrilldown') {
      const dtcSection = formFields?.find(section => section?.sectionKey === 'dtcDetails');

      dtcSection?.fields?.forEach(field => {
        if (['fmi', 'spn'].includes(field.key)) {
          field.disabled = true;
        }
      });
    }

    this.content = {
      entityType: EntityValue.DTC,
      pageType: EntityPage.DTC,
      formField: formFields,
      formResponse: this._entityDeviceOnboardFormConfig?.formResponse,
      bulkUploadEnable:
        this.dialogData?.type === 'dtcDrilldown'
          ? this._apiService.rbacManagement('case_Casedrilldownedit_DTCBulkUpload')
          : this._apiService.rbacManagement('dtc_DTCAdd_DTCBulkAdd'),
      bulkUploadButtonLabel: "Bulk Upload",
      bulkUploadTitle: "Bulk DTC Upload",
      bulkUploadSubtitle: "Update DTC Master Data",
      apiType: "dtcBulkUpload",
      inventoryApiType: "dtcDownload",
      formStatus: this.dialogData?.formStatus,
      modalType: this.dialogData?.type,
      data: this.dialogData?.data
    };
  }

  /**
   * Closes dialog
   */
  closeDialog() {
    this.dialogClose?.emit();
  }

  /**
   * Sets form values
   * @param event 
   */
  setFormValues(event: any) {
    const { dtcDetails = {}, vehicleGroup, specification = {} } = event || {};
    const { severity, ruleCategory, caseRules } = specification;

    const reqPayload = {
      ...dtcDetails,
      severity,
      ruleCategory,
      vehicleGroup,
      caseRules,
      ...(this.dialogData?.type === 'dtcDrilldown' && {dtcUnknown : false}),
      ...(this.dialogData?.type === 'dtcDrilldown' && {isPromotedFromNoDTC : true})
    };
    if(this.dialogData?.type === 'dtcDrilldown') {
      this.updateDtc(reqPayload,this.dialogData?.data?.dtcDetails?._id);
    } else {
      this.addDtc(reqPayload);
    }
  }

  /**
  * Updates dtc
  * @param reqPayload 
  */
  updateDtc(reqPayload : any, id:string) {
    this._updateDtcSubscription = this._dtcService.updateDtc(reqPayload, id)?.subscribe(
      {
        next: (response: any) => {
          this._apiService.toasterService({
            type: 'success',
            msg: 'DTC updated successfully'
          });
          this.dialogClose.emit(true);
        }, error: () => {
          this._apiService.toasterService({
            type: 'error',
            msg: Messages.ERROR
          });
        }
      });
  }

  /**
  * Adds dtc
  * @param reqPayload 
  */
  addDtc(reqPayload: any) {
    this._addDtcSubscription = this._dtcService.addDtc(reqPayload)?.subscribe(
      {
        next: (response: any) => {
          if (response) {
            this._apiService.toasterService({
              type: 'success',
              msg: 'DTC added successfully'
            });
            this._router.navigate(['dtc']);
          }
        }, error: () => {
          this._apiService.toasterService({
            type: 'error',
            msg: Messages.ERROR
          });
        }
      }
    );
  }

}
